// src/AuthProvider.tsx
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from './utils/supabaseClient';
import config from './config';
import { Session, User, AuthChangeEvent, UserAppMetadata } from '@supabase/supabase-js';
import { trackUserAuth } from './utils/analytics';
import ProfileModal from './components/ProfileModal';

export interface UserProfileData {
  id: number;
  supabase_uid: string;
  display_name: string | null;
  gardens_submitted: number;
  total_points: number;
  show_on_leaderboards: boolean;
  date_joined: string;
  is_anonymous_profile?: boolean;
  is_staff?: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfileData | null;
  loading: boolean;
  isAnonymous: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [session, setSession] = useState<Session | null>(null);
  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
  const [showProfileSetupModal, setShowProfileSetupModal] = useState<boolean>(false);
  const [isGoogleUser, setIsGoogleUser] = useState<boolean>(false);
  const [googleDisplayName, setGoogleDisplayName] = useState<string>('');

  const fetchUserProfile = useCallback(async (currentSession: Session | null, isNewUserRegistration?: boolean) => {
    if (!currentSession || !currentSession.access_token || currentSession.user?.is_anonymous) {
      setProfile(null);
      if(!currentSession || currentSession.user?.is_anonymous) {
        setShowProfileSetupModal(false);
      }
      return;
    }

    try {
      let baseUrl = config.apiBaseUrl;
      if (baseUrl.endsWith('/')) {
        baseUrl = baseUrl.slice(0, -1);
      }
      const apiUrl = `${baseUrl}/get_user_profile/`;
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${currentSession.access_token}`
        }
      });

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const data = await response.json();
        
        if (data.status === 'success' && data.profile) {
          setProfile(data.profile);
        } else {
          console.error('API Error or profile missing:', data.message || data.error);
          setProfile(null);
        }
      } else {
        const textResponse = await response.text();
        console.error('Non-JSON response from /get_user_profile/:', textResponse.substring(0, 200) + "...");
        setProfile(null);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    const processSession = async (currentSession: Session | null, eventType?: AuthChangeEvent) => {
      const supabaseUser = currentSession?.user || null;
      setUser(supabaseUser);
      setSession(currentSession);
      setIsAnonymous(supabaseUser?.is_anonymous === true);

      const provider = (supabaseUser?.app_metadata as UserAppMetadata)?.provider as string;
      const isGoogle = provider === 'google';
      setIsGoogleUser(isGoogle);

      if (isGoogle && supabaseUser?.user_metadata?.full_name) {
        setGoogleDisplayName(supabaseUser.user_metadata.full_name);
      }
      
      if (eventType === 'SIGNED_IN') {
          await fetchUserProfile(currentSession, false);
          trackUserAuth(true, provider || (supabaseUser?.is_anonymous ? 'anonymous' : 'unknown_signed_in'));
      } else if (eventType === 'SIGNED_OUT') {
          setProfile(null);
          setShowProfileSetupModal(false);
          trackUserAuth(false);
          await supabase.auth.signInAnonymously();
      } else if (currentSession) {
          await fetchUserProfile(currentSession, false);
      }
      
      if (!currentSession && eventType !== 'SIGNED_OUT') {
        const { error } = await supabase.auth.signInAnonymously();
        if (error) {
          console.error('Error signing in anonymously:', error);
          setUser(null);
          setSession(null);
          setIsAnonymous(false);
        }
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      processSession(session, undefined).finally(() => {
        setLoading(false);
      });
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        await processSession(session, event);
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, [fetchUserProfile]);

  const refreshProfile = useCallback(async () => {
    if (session && !session.user?.is_anonymous) {
      await fetchUserProfile(session, false);
    }
  }, [session, fetchUserProfile]);
  
  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({ 
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/welcome`
      }
    });
    if (error) {
      console.error("Google Sign In Error:", error.message);
    }
  };
  
  const signInWithEmail = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({ 
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/welcome`
      }
    });
    if (error) {
      console.error("Email Sign In Error:", error.message);
    }
  };
  
  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
        console.error("Logout Error:", error.message);
    }
  };
  
  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        isAnonymous,
        signInWithGoogle,
        signInWithEmail,
        logout,
        refreshProfile,
      }}
    >
      {children}
      {showProfileSetupModal && user && !isAnonymous && (
        <ProfileModal
          open={showProfileSetupModal}
          onClose={() => {
            setShowProfileSetupModal(false);
            refreshProfile();
          }}
        />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};